import {
  STOREFRONT_API_PATHS,
  storefrontApiPath,
  type CustomerAddress,
  type CustomerOrderCancellation,
  type CustomerAddressInput,
  type CustomerAuthSession,
  type CustomerLoginRequest,
  type CustomerLogoutResponse,
  type CustomerOtpStartRequest,
  type CustomerOtpStartResponse,
  type CustomerOtpCompleteRequest,
  type CustomerOtpCompleteResponse,
  type CustomerOtpVerifyRequest,
  type CustomerOtpVerifyResponse,
  type CustomerProfile,
  type CustomerProfileUpdateRequest,
  type CustomerRegisterRequest,
  type DeliveryCheck,
  type ServiceType,
  type StorefrontBootstrap,
  type StorefrontBranch,
  type StorefrontPromotionAttribution,
  type StorefrontCompactMenu,
  type StorefrontDeliveryZone,
  type StorefrontMenu,
  type StorefrontLoyaltyWallet,
  type StorefrontCreateOrderResponse,
  type StorefrontOrderHistoryDetail,
  type StorefrontOrderHistoryItem,
  type StorefrontOrderRequest,
  type StorefrontPaymentAttempt,
  type StorefrontPaymentLink,
  type StorefrontProduct,
  type StorefrontQuote,
  type StorefrontQuoteRequest,
} from "@emynor/storefront-contract";

export * from "@emynor/storefront-contract";

export type StorefrontSdkOptions = {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  previewToken?: string | null;
  developmentTenantHost?: string;
  onAuthStateChange?: (state: StorefrontAuthState) => void;
};

export type StorefrontAuthState = { authenticated: boolean };
export type StorefrontRequestOptions = { signal?: AbortSignal };

export class StorefrontSdkError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;
  readonly requestId?: string;

  constructor(input: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
  }) {
    super(input.message);
    this.name = "StorefrontSdkError";
    this.code = input.code;
    this.status = input.status;
    if (input.details !== undefined) this.details = input.details;
    if (input.requestId !== undefined) this.requestId = input.requestId;
  }
}

type RequestPolicy = {
  authentication?: "none" | "optional" | "required";
  durable?: boolean;
  allowRefresh?: boolean;
  conditionalCache?: boolean;
};

export class StorefrontSdk {
  readonly isPreview: boolean;
  private readonly baseUrl: string;
  private readonly fetcher: typeof globalThis.fetch;
  private readonly previewToken: string | null;
  private readonly developmentTenantHost?: string;
  private readonly authListeners = new Set<
    (state: StorefrontAuthState) => void
  >();
  private readonly representationCache = new Map<
    string,
    { etag: string; data: unknown }
  >();
  private accessToken: string | null = null;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(options: StorefrontSdkOptions = {}) {
    this.baseUrl = (options.baseUrl ?? "").replace(/\/$/, "");
    const fetcher = options.fetch ?? globalThis.fetch?.bind(globalThis);

    if (!fetcher) {
      throw new Error("No fetch implementation is available.");
    }

    this.fetcher = fetcher;
    this.previewToken = options.previewToken ?? null;
    this.isPreview = Boolean(this.previewToken);
    if (options.developmentTenantHost)
      this.developmentTenantHost = options.developmentTenantHost;
    if (options.onAuthStateChange)
      this.authListeners.add(options.onAuthStateChange);
  }

  get authState(): StorefrontAuthState {
    return { authenticated: Boolean(this.accessToken) };
  }

  subscribeAuth(listener: (state: StorefrontAuthState) => void): () => void {
    this.authListeners.add(listener);
    listener(this.authState);
    return () => this.authListeners.delete(listener);
  }

  clearSession(): void {
    this.setAccessToken(null);
  }

  async restoreSession(): Promise<boolean> {
    if (this.isPreview) return false;
    return Boolean(await this.refreshAccessToken());
  }

  bootstrap(options?: StorefrontRequestOptions) {
    return this.request<StorefrontBootstrap>(
      STOREFRONT_API_PATHS.bootstrap,
      requestInit("GET", undefined, options),
      { authentication: "none", conditionalCache: true },
    );
  }

  branches(options?: StorefrontRequestOptions) {
    return this.request<StorefrontBranch[]>(
      STOREFRONT_API_PATHS.branches,
      requestInit("GET", undefined, options),
      { authentication: "none" },
    );
  }

  deliveryZones(options?: StorefrontRequestOptions) {
    return this.request<StorefrontDeliveryZone[]>(
      STOREFRONT_API_PATHS.deliveryZones,
      requestInit("GET", undefined, options),
      { authentication: "none" },
    );
  }

  deliveryCheck(
    location: { lat: number; lng: number },
    options?: StorefrontRequestOptions,
  ) {
    return this.request<DeliveryCheck>(
      STOREFRONT_API_PATHS.deliveryCheck,
      requestInit("POST", { location }, options),
      { authentication: "none" },
    );
  }

  menu(
    branchId: string,
    orderType: ServiceType,
    options?: StorefrontRequestOptions,
  ) {
    const query = new URLSearchParams({ branchId, orderType });
    return this.request<StorefrontMenu>(
      `${STOREFRONT_API_PATHS.menu}?${query}`,
      requestInit("GET", undefined, options),
      { authentication: "none", conditionalCache: true },
    );
  }

  compactMenu(
    branchId: string,
    orderType: ServiceType,
    options?: StorefrontRequestOptions,
  ) {
    const query = new URLSearchParams({ branchId, orderType });
    return this.request<StorefrontCompactMenu>(
      `${STOREFRONT_API_PATHS.compactMenu}?${query}`,
      requestInit("GET", undefined, options),
      { authentication: "none", conditionalCache: true },
    );
  }

  product(
    slug: string,
    context?: { branchId: string; orderType: ServiceType },
    options?: StorefrontRequestOptions,
  ) {
    const query = context ? `?${new URLSearchParams(context)}` : "";
    return this.request<StorefrontProduct>(
      `${storefrontApiPath.product(slug)}${query}`,
      requestInit("GET", undefined, options),
      { authentication: "none", conditionalCache: true },
    );
  }

  offerAttribution(token: string, options?: StorefrontRequestOptions) {
    return this.request<StorefrontPromotionAttribution>(
      storefrontApiPath.offerAttribution(token),
      requestInit("POST", undefined, options),
      { authentication: "none" },
    );
  }

  quote(input: StorefrontQuoteRequest, options?: StorefrontRequestOptions) {
    return this.request<StorefrontQuote>(
      STOREFRONT_API_PATHS.quote,
      requestInit("POST", input, options),
      { authentication: "optional" },
    );
  }

  createOrder(
    input: StorefrontOrderRequest,
    options?: StorefrontRequestOptions,
  ) {
    return this.request<StorefrontCreateOrderResponse>(
      STOREFRONT_API_PATHS.orders,
      requestInit("POST", input, options),
      { authentication: "required", durable: true },
    );
  }

  paymentAttempt(attemptId: string, options?: StorefrontRequestOptions) {
    return this.request<StorefrontPaymentAttempt>(
      storefrontApiPath.paymentAttempt(attemptId),
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  reconcilePaymentAttempt(
    attemptId: string,
    options?: StorefrontRequestOptions,
  ) {
    return this.request<{ reconciliationQueued: boolean }>(
      storefrontApiPath.paymentAttemptReconciliation(attemptId),
      requestInit("POST", undefined, options),
      { authentication: "required", durable: true },
    );
  }

  retryPaymentAttempt(
    attemptId: string,
    input: { idempotencyKey: string },
    options?: StorefrontRequestOptions,
  ) {
    return this.request<StorefrontPaymentAttempt>(
      storefrontApiPath.paymentAttemptRetry(attemptId),
      requestInit("POST", input, options),
      { authentication: "required", durable: true },
    );
  }

  paymentLink(token: string, options?: StorefrontRequestOptions) {
    return this.request<StorefrontPaymentLink>(
      storefrontApiPath.paymentLink(token),
      requestInit("GET", undefined, options),
      { authentication: "none" },
    );
  }

  async register(
    input: CustomerRegisterRequest,
    options?: StorefrontRequestOptions,
  ) {
    const session = await this.request<CustomerAuthSession>(
      STOREFRONT_API_PATHS.customerRegister,
      requestInit("POST", input, options),
      { authentication: "none", durable: true },
    );
    this.setAccessToken(session.accessToken);
    return session;
  }

  async login(input: CustomerLoginRequest, options?: StorefrontRequestOptions) {
    const session = await this.request<CustomerAuthSession>(
      STOREFRONT_API_PATHS.customerLogin,
      requestInit("POST", input, options),
      { authentication: "none", durable: true },
    );
    this.setAccessToken(session.accessToken);
    return session;
  }

  startOtp(input: CustomerOtpStartRequest, options?: StorefrontRequestOptions) {
    return this.request<CustomerOtpStartResponse>(
      STOREFRONT_API_PATHS.customerOtpStart,
      requestInit("POST", input, options),
      { authentication: "none", durable: true },
    );
  }

  async verifyOtp(
    input: CustomerOtpVerifyRequest,
    options?: StorefrontRequestOptions,
  ) {
    const response = await this.request<CustomerOtpVerifyResponse>(
      STOREFRONT_API_PATHS.customerOtpVerify,
      requestInit("POST", input, options),
      { authentication: "none", durable: true },
    );
    if (response.status === "authenticated") this.setAccessToken(response.accessToken);
    return response;
  }

  async completeOtpRegistration(
    input: CustomerOtpCompleteRequest,
    options?: StorefrontRequestOptions,
  ) {
    const response = await this.request<CustomerOtpCompleteResponse>(
      STOREFRONT_API_PATHS.customerOtpComplete,
      requestInit("POST", input, options),
      { authentication: "none", durable: true },
    );
    this.setAccessToken(response.accessToken);
    return response;
  }

  async logout(
    options?: StorefrontRequestOptions,
  ): Promise<CustomerLogoutResponse> {
    try {
      return await this.request<CustomerLogoutResponse>(
        STOREFRONT_API_PATHS.customerLogout,
        requestInit("POST", undefined, options),
        { authentication: "required", durable: true },
      );
    } finally {
      this.clearSession();
    }
  }

  profile(options?: StorefrontRequestOptions) {
    return this.request<CustomerProfile>(
      STOREFRONT_API_PATHS.customerProfile,
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  updateProfile(
    input: CustomerProfileUpdateRequest,
    options?: StorefrontRequestOptions,
  ) {
    return this.request<CustomerProfile>(
      STOREFRONT_API_PATHS.customerProfile,
      requestInit("PATCH", input, options),
      { authentication: "required", durable: true },
    );
  }

  addresses(options?: StorefrontRequestOptions) {
    return this.request<CustomerAddress[]>(
      STOREFRONT_API_PATHS.customerAddresses,
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  loyaltyWallet(options?: StorefrontRequestOptions) {
    return this.request<StorefrontLoyaltyWallet>(
      STOREFRONT_API_PATHS.customerLoyalty,
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  createAddress(
    input: CustomerAddressInput,
    options?: StorefrontRequestOptions,
  ) {
    return this.request<CustomerAddress>(
      STOREFRONT_API_PATHS.customerAddresses,
      requestInit("POST", input, options),
      { authentication: "required", durable: true },
    );
  }

  updateAddress(
    addressId: string,
    input: CustomerAddressInput,
    options?: StorefrontRequestOptions,
  ) {
    return this.request<CustomerAddress>(
      storefrontApiPath.customerAddress(addressId),
      requestInit("PUT", input, options),
      { authentication: "required", durable: true },
    );
  }

  async deleteAddress(
    addressId: string,
    options?: StorefrontRequestOptions,
  ): Promise<void> {
    await this.request<void>(
      storefrontApiPath.customerAddress(addressId),
      requestInit("DELETE", undefined, options),
      { authentication: "required", durable: true },
    );
  }

  orders(options?: StorefrontRequestOptions) {
    return this.request<StorefrontOrderHistoryItem[]>(
      STOREFRONT_API_PATHS.customerOrders,
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  order(orderId: string, options?: StorefrontRequestOptions) {
    return this.request<StorefrontOrderHistoryDetail>(
      storefrontApiPath.customerOrder(orderId),
      requestInit("GET", undefined, options),
      { authentication: "required" },
    );
  }

  cancelOrder(orderId: string, options?: StorefrontRequestOptions) {
    return this.request<CustomerOrderCancellation>(
      storefrontApiPath.customerOrderCancellation(orderId),
      requestInit("POST", {}, options),
      { authentication: "required", durable: true },
    );
  }

  private async request<T>(
    path: string,
    init: RequestInit,
    policy: RequestPolicy = {},
  ): Promise<T> {
    const authentication = policy.authentication ?? "none";
    if (this.isPreview && (policy.durable || authentication === "required"))
      throw previewActionError();
    const headers = new Headers(init.headers);
    if (
      init.body !== undefined &&
      init.body !== null &&
      !headers.has("content-type")
    ) {
      headers.set("content-type", "application/json");
    }
    if (this.previewToken)
      headers.set("x-storefront-preview-token", this.previewToken);
    if (this.developmentTenantHost)
      headers.set("x-tenant-host", this.developmentTenantHost);
    if (this.accessToken && authentication !== "none")
      headers.set("authorization", `Bearer ${this.accessToken}`);
    const cached = policy.conditionalCache
      ? this.representationCache.get(path)
      : undefined;
    if (cached) headers.set("if-none-match", cached.etag);
    const response = await this.fetcher(`${this.baseUrl}${path}`, {
      ...init,
      credentials:
        authentication === "none" && !policy.durable ? "omit" : "include",
      headers,
    });
    if (response.status === 304) {
      if (cached) return cached.data as T;
      throw new StorefrontSdkError({
        code: "invalid_api_response",
        message: "The storefront API returned an invalid cache response",
        status: 502,
      });
    }
    if (
      response.status === 401 &&
      authentication !== "none" &&
      policy.allowRefresh !== false
    ) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed)
        return this.request<T>(path, init, { ...policy, allowRefresh: false });
    }
    const payload =
      response.status === 204
        ? null
        : ((await response.json().catch(() => null)) as unknown);
    if (!response.ok) throw responseError(response, payload);
    if (response.status === 204) return undefined as T;
    if (!payload || typeof payload !== "object" || !("data" in payload)) {
      const requestId = response.headers.get("x-request-id");
      throw new StorefrontSdkError({
        code: "invalid_api_response",
        message: "The storefront API returned an invalid response",
        status: 502,
        ...(requestId ? { requestId } : {}),
      });
    }
    const data = (payload as { data: T }).data;
    const etag = policy.conditionalCache ? response.headers.get("etag") : null;
    if (etag) {
      this.representationCache.delete(path);
      this.representationCache.set(path, { etag, data });
      while (this.representationCache.size > 32) {
        const oldest = this.representationCache.keys().next().value as
          string | undefined;
        if (!oldest) break;
        this.representationCache.delete(oldest);
      }
    }
    return data;
  }

  private refreshAccessToken(): Promise<string | null> {
    this.refreshPromise ??= this.request<CustomerAuthSession>(
      STOREFRONT_API_PATHS.customerRefresh,
      requestInit("POST"),
      { authentication: "none", durable: true, allowRefresh: false },
    )
      .then((session) => {
        this.setAccessToken(session.accessToken);
        return session.accessToken;
      })
      .catch(() => {
        this.clearSession();
        return null;
      })
      .finally(() => {
        this.refreshPromise = null;
      });
    return this.refreshPromise;
  }

  private setAccessToken(token: string | null): void {
    const changed = Boolean(this.accessToken) !== Boolean(token);
    this.accessToken = token;
    if (changed)
      for (const listener of this.authListeners) listener(this.authState);
  }
}

export function createStorefrontSdk(
  options: StorefrontSdkOptions = {},
): StorefrontSdk {
  return new StorefrontSdk(options);
}

export function createCompactMenuLookups(menu: StorefrontCompactMenu) {
  return {
    categoriesById: new Map(
      menu.categories.map((category) => [category.id, category]),
    ),
    itemsBySlug: new Map(menu.items.map((item) => [item.slug, item])),
    addonGroupsById: new Map(
      menu.addonGroups.map((group) => [group.id, group]),
    ),
    addonsById: new Map(menu.addons.map((addon) => [addon.id, addon])),
  };
}

export function hydrateCompactMenu(
  menu: StorefrontCompactMenu,
): StorefrontMenu {
  const lookups = createCompactMenuLookups(menu);
  const hydrateGroups = (
    assignments: StorefrontCompactMenu["items"][number]["addonGroups"],
  ) =>
    assignments.map((assignment) => {
      const group = lookups.addonGroupsById.get(assignment.groupId);
      if (!group)
        throw invalidCompactMenu(`Missing add-on group ${assignment.groupId}`);
      return {
        ...group,
        minimum: assignment.minimum,
        maximum: assignment.maximum,
        options: assignment.optionIds.map((optionId) => {
          const option = lookups.addonsById.get(optionId);
          if (!option || option.group_id !== assignment.groupId) {
            throw invalidCompactMenu(
              `Missing add-on option ${optionId} for group ${assignment.groupId}`,
            );
          }
          return option;
        }),
      };
    });
  return {
    ...(menu.nextSaleChangeAt !== undefined
      ? { nextSaleChangeAt: menu.nextSaleChangeAt }
      : {}),
    categories: menu.categories,
    checkoutSuggestionItemIds: menu.checkoutSuggestionItemIds ?? [],
    items: menu.items.map(({ addonGroups, variants, ...item }) => ({
      ...item,
      addon_groups: hydrateGroups(addonGroups),
      variants: variants.map(({ addonGroups: variantGroups, ...variant }) => ({
        ...variant,
        addon_groups: hydrateGroups(variantGroups),
      })),
    })),
  };
}

function invalidCompactMenu(message: string): StorefrontSdkError {
  return new StorefrontSdkError({
    code: "storefront_contract_invalid",
    message,
    status: 502,
  });
}

export function consumeStorefrontPreviewToken(input: {
  location: Pick<Location, "href">;
  history: Pick<History, "replaceState">;
}): string | null {
  const url = new URL(input.location.href);
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
  const token = fragment.get("token");
  if (!token) return null;
  input.history.replaceState(null, "", `${url.pathname}${url.search}`);
  if (!/^[A-Za-z0-9_-]{32,256}$/.test(token)) {
    throw new StorefrontSdkError({
      code: "storefront_preview_invalid",
      message: "The preview session is invalid",
      status: 401,
    });
  }
  return token;
}

function requestInit(
  method: string,
  body?: unknown,
  options?: StorefrontRequestOptions,
): RequestInit {
  return {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(options?.signal ? { signal: options.signal } : {}),
  };
}

function responseError(
  response: Response,
  payload: unknown,
): StorefrontSdkError {
  const candidate =
    payload && typeof payload === "object" && "error" in payload
      ? (payload as { error?: unknown }).error
      : undefined;
  const error =
    candidate && typeof candidate === "object"
      ? (candidate as Record<string, unknown>)
      : {};
  return new StorefrontSdkError({
    code: typeof error["code"] === "string" ? error["code"] : "request_failed",
    message:
      typeof error["message"] === "string"
        ? error["message"]
        : `Storefront request failed (${response.status})`,
    status: response.status,
    ...(error["details"] !== undefined ? { details: error["details"] } : {}),
    ...(response.headers.get("x-request-id")
      ? { requestId: response.headers.get("x-request-id")! }
      : {}),
  });
}

function previewActionError(): StorefrontSdkError {
  return new StorefrontSdkError({
    code: "storefront_preview_action_disabled",
    message:
      "Customer accounts, payments, and orders are disabled in storefront preview",
    status: 409,
  });
}
